/**
 * #605 — a drag from Papan's own page (the drive, the project explorer, a tab)
 * onto an app running in this viewer.
 *
 * The browser does not deliver it: the app's sandboxed frame runs in another
 * process of the same tab, and Chromium refuses a drag STARTED in the page to
 * any other process of it (a drag from the desktop is not the page's, and
 * arrives as usual). So while such a drag is on, a transparent catcher lies
 * over the app's frame — in this page, where the drag is allowed — and relays
 * it to the app (`papan:files:drag`), whose SDK replays it there as the
 * ordinary `dragenter` / `dragover` / `drop` events at the same point. The
 * drag's token goes only with the drop, as a native drop would carry it; the
 * app redeems it through the host as always (`papan:files:drop`).
 *
 * Only a drag carrying Papan's handoff type (`HANDOFF_DRAG_MIME`, set by
 * `offerDragToApps`) raises the catcher — anything else keeps reaching the
 * app natively — and it never outlives the drag, or it would eat the app's
 * clicks.
 */
import { useEffect, useRef, useState, type DragEvent as ReactDragEvent, type ReactElement } from 'react'
import { HANDOFF_DRAG_MIME } from '@papan/types'
import { frameMessageTarget } from './app-origin-frame.js'

/** Past this long without a drag event in this page, the drag has gone
 *  (left the window, or ended in another one) — the catcher comes down.
 *  A live drag fires `dragover` every few hundred ms even when still. */
const STALE_DRAG_MS = 1_500

const isHandoffDrag = (e: DragEvent | ReactDragEvent) =>
  !!e.dataTransfer && Array.from(e.dataTransfer.types).includes(HANDOFF_DRAG_MIME)

export function useHandoffDragRelay({ enabled, frame, runsSdk }: {
  /** A serve-session app (one that can run the SDK) is showing. */
  enabled: boolean
  /** The app frame on screen — the relay's only recipient. */
  frame: () => HTMLIFrameElement | null
  /** Whether that frame's page runs the SDK (it said hello): no catcher over a
   *  page that could not take the drop anyway — it would only swallow it. */
  runsSdk: (win: Window) => boolean
}): ReactElement | null {
  const [active, setActive] = useState(false)
  const lastSeenRef = useRef(0)
  const catcherRef = useRef<HTMLDivElement>(null)
  const frameRef = useRef(frame)
  frameRef.current = frame
  const runsSdkRef = useRef(runsSdk)
  runsSdkRef.current = runsSdk

  useEffect(() => {
    if (!enabled) { setActive(false); return }
    const seen = (e: DragEvent) => {
      if (!isHandoffDrag(e)) return
      const win = frameRef.current()?.contentWindow
      if (!win || !runsSdkRef.current(win)) return
      lastSeenRef.current = Date.now()
      setActive(true)
    }
    // A drop ON the catcher is its own to relay: taken down here first (a
    // capture listener, then React re-renders before the catcher's handler
    // runs), the drop would reach nothing.
    const end = (e: Event) => { if (e.target !== catcherRef.current) setActive(false) }
    window.addEventListener('dragenter', seen, true)
    window.addEventListener('dragover', seen, true)
    window.addEventListener('dragend', end, true)
    window.addEventListener('drop', end, true)
    return () => {
      window.removeEventListener('dragenter', seen, true)
      window.removeEventListener('dragover', seen, true)
      window.removeEventListener('dragend', end, true)
      window.removeEventListener('drop', end, true)
    }
  }, [enabled])

  // The drag left without an end event this page could hear.
  useEffect(() => {
    if (!active) return
    const timer = setInterval(() => {
      if (Date.now() - lastSeenRef.current > STALE_DRAG_MS) setActive(false)
    }, 500)
    return () => clearInterval(timer)
  }, [active])

  if (!enabled || !active) return null

  /** Post to the app on screen, at the point in ITS viewport. The frame is a
   *  sandboxed (opaque) origin, so no origin can be named: `'*'`, to that one
   *  window — as the relay's own replies go. */
  const relay = (e: ReactDragEvent<HTMLDivElement>, phase: 'over' | 'leave' | 'drop', token?: string) => {
    const target = frameRef.current()
    if (!target?.contentWindow) return
    const box = target.getBoundingClientRect()
    const message = { type: 'papan:files:drag', phase, x: e.clientX - box.left, y: e.clientY - box.top, ...(token ? { token } : {}) }
    try { target.contentWindow.postMessage(message, frameMessageTarget(target)) } catch { /* gone */ }
  }
  const over = (e: ReactDragEvent<HTMLDivElement>) => {
    if (!isHandoffDrag(e)) return
    // Ours alone: never also the editor's own drop handling around the viewer
    // (which would open the dragged files in tabs).
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'copy'
    relay(e, 'over')
  }
  return (
    <div
      ref={catcherRef}
      data-testid="handoff-drag-catcher"
      aria-hidden
      style={{ position: 'absolute', inset: 0, zIndex: 2 }}
      onDragEnter={over}
      onDragOver={over}
      onDragLeave={(e) => {
        e.stopPropagation()
        // Out of the catcher, not into a child of it (it has none; kept honest).
        if (!(e.relatedTarget instanceof Node && e.currentTarget.contains(e.relatedTarget))) relay(e, 'leave')
      }}
      onDrop={(e) => {
        if (!isHandoffDrag(e)) return
        e.preventDefault()
        e.stopPropagation()
        relay(e, 'drop', e.dataTransfer.getData(HANDOFF_DRAG_MIME))
        setActive(false)
      }}
    />
  )
}
